import { useEffect, useState } from "react";

import { toast } from "~/components/ui/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { Profile, UserFormData } from "~/pages/admin/types";
import { useProfileStore } from "~/store/profileStore";
import { UserRole } from "~/types/user";

// Fix the type error related to the RPC functions
// Add type declaration that properly includes our custom functions
declare module "@supabase/supabase-js" {
  interface SupabaseClient {
    rpc<T = unknown>(
      fn: string,
      params?: object,
      options?: object
    ): Promise<{ data: T; error: Error | null }>;
  }
}

export const useUsers = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);

  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] =
    useState(false);
  const [currentProfile, setCurrentProfile] = useState<Profile | null>(null);
  const [profileToDelete, setProfileToDelete] = useState<Profile | null>(null);
  const [profileToDeactivate, setProfileToDeactivate] =
    useState<Profile | null>(null);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [profileToChangePassword, setProfileToChangePassword] =
    useState<Profile | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState<Set<string>>(
    new Set()
  );

  const { id: currentUserId, role: currentUserRole } = useProfileStore();
  const [formData, setFormData] = useState<UserFormData>({
    email: "",
    name: "",
    lastname: "",
    phone: "",
    role: "seller" as UserRole,
    password: "",
  });

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      setLoading(true);

      // Attempt to get current user's role first to confirm admin status
      const { data: currentUserData, error: currentUserError } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", currentUserId)
        .single();

      if (currentUserError) {
        console.error("Error fetching current user:", currentUserError);
        toast({
          title: "Error",
          description: "No se pudo verificar tus permisos de administrador",
          variant: "destructive",
        });
        return;
      }

      // Check if user is admin or superadmin
      const isAdminUser =
        currentUserData?.role === "admin" ||
        currentUserData?.role === "superadmin";

      if (!isAdminUser) {
        toast({
          title: "Acceso denegado",
          description: "No tienes permisos para ver la lista de usuarios",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // If we're here, user has admin permissions, fetch all profiles using RPC function
      // This bypasses RLS issues that were preventing admins from seeing all users
      const { data, error } = await supabase.rpc("admin_get_all_profiles");

      if (error) {
        console.error("Error fetching profiles:", error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los usuarios",
          variant: "destructive",
        });
      } else {
        const typedProfiles =
          data?.map((profile) => ({
            ...profile,
            role: profile.role as UserRole,
          })) || [];
        setProfiles(typedProfiles);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Selection handling
  const toggleProfileSelection = (profileId: string) => {
    setSelectedProfiles((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(profileId)) {
        newSelection.delete(profileId);
      } else {
        newSelection.add(profileId);
      }
      return newSelection;
    });
  };

  const toggleAllProfiles = (selected: boolean) => {
    if (selected) {
      // Do not select the current user or superadmins if not a superadmin
      const selectableProfiles = profiles
        .filter(
          (profile) =>
            profile.id !== currentUserId &&
            (currentUserRole === "superadmin" || profile.role !== "superadmin")
        )
        .map((profile) => profile.id);

      setSelectedProfiles(new Set(selectableProfiles));
    } else {
      setSelectedProfiles(new Set());
    }
  };

  // Password change functionality
  const handleChangePassword = (profile: Profile) => {
    if (profile.id === currentUserId) {
      toast({
        title: "Acción no permitida",
        description:
          "No puedes cambiar tu propia contraseña desde aquí. Usa la configuración de tu perfil.",
        variant: "destructive",
      });
      return;
    }

    if (profile.role === "superadmin" && currentUserRole !== "superadmin") {
      toast({
        title: "Permiso denegado",
        description:
          "Solo un superadmin puede cambiar la contraseña de otro superadmin",
        variant: "destructive",
      });
      return;
    }

    setProfileToChangePassword(profile);
    setIsChangePasswordDialogOpen(true);
  };

  const confirmChangePassword = async (newPassword: string) => {
    if (!profileToChangePassword) return;

    try {
      setChangePasswordLoading(true);
      console.log(
        "Attempting to change password for user:",
        profileToChangePassword.id
      );

      // First try using Supabase admin API
      try {
        const { error: adminError } = await supabase.auth.admin.updateUserById(
          profileToChangePassword.id,
          { password: newPassword }
        );

        if (adminError) {
          console.error("Admin API error:", adminError);
          throw adminError;
        }

        toast({
          title: "Contraseña actualizada",
          description: `La contraseña de ${profileToChangePassword.name} ${profileToChangePassword.lastname} se ha cambiado correctamente.`,
        });

        closeChangePasswordDialog();
        return;
      } catch (adminError) {
        console.error("Admin API failed, trying SQL approach:", adminError);

        // Fallback to direct SQL update
        // Note: This requires proper service role permissions
        const { error: sqlError } = await supabase.rpc(
          "update_user_password_by_id",
          {
            user_id: profileToChangePassword.id,
            new_password: newPassword,
          }
        );

        if (sqlError) {
          console.error("SQL password update error:", sqlError);

          // If SQL function doesn't exist, show informative message
          if (
            sqlError.message?.includes("function") &&
            sqlError.message?.includes("does not exist")
          ) {
            toast({
              title: "Función no disponible",
              description:
                "La función de cambio de contraseña no está configurada en la base de datos. Contacta al administrador del sistema.",
              variant: "destructive",
            });
            return;
          }

          throw sqlError;
        }

        toast({
          title: "Contraseña actualizada",
          description: `La contraseña de ${profileToChangePassword.name} ${profileToChangePassword.lastname} se ha cambiado correctamente.`,
        });

        closeChangePasswordDialog();
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Error desconocido";
      console.error("Error changing password:", error);

      toast({
        title: "Error",
        description: "No se pudo cambiar la contraseña: " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const closeChangePasswordDialog = () => {
    setIsChangePasswordDialogOpen(false);
    setProfileToChangePassword(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRoleChange = (value: string) => {
    setFormData({ ...formData, role: value as UserRole });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // First try using our new safe function that handles orphaned entries
      const { data, error } = (await supabase.rpc(
        "create_user_with_profile_safe",
        {
          p_email: formData.email,
          p_password: formData.password,
          p_name: formData.name,
          p_lastname: formData.lastname,
          p_role: formData.role,
          p_job_title: null,
          p_department: null,
          p_phone: formData.phone || null,
        }
      )) as { data: string | null; error: Error | null };

      if (error) {
        console.error("Error creating user:", error);

        // If the safe function fails, fall back to the admin function
        if (error.message?.includes("does not exist")) {
          console.log("Falling back to create_admin_user function");
          const { data: fallbackData, error: fallbackError } =
            (await supabase.rpc("create_admin_user", {
              p_email: formData.email,
              p_password: formData.password,
              p_name: formData.name,
              p_lastname: formData.lastname,
              p_role: formData.role,
              p_phone: formData.phone || null,
            })) as { data: string | null; error: Error | null };

          if (fallbackError) {
            throw fallbackError;
          }
        } else {
          // Check for specific permission errors
          if (error.message?.includes("Permission denied")) {
            toast({
              title: "Permiso denegado",
              description: error.message,
              variant: "destructive",
            });
            return;
          }

          // Check for duplicate user errors and provide helpful message
          if (error.message?.includes("already exists")) {
            toast({
              title: "Usuario ya existe",
              description: `Un usuario con el email ${formData.email} ya existe en el sistema.`,
              variant: "destructive",
            });
            return;
          }

          toast({
            title: "Error",
            description: "No se pudo crear el usuario: " + error.message,
            variant: "destructive",
          });
          return;
        }
      }

      toast({
        title: "Usuario creado",
        description: "El usuario se ha creado correctamente",
      });

      resetForm();
      fetchProfiles();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Error desconocido";
      console.error("Error creating user:", error);

      // Handle the specific constraint violation error
      if (errorMessage.includes("users_email_partial_key")) {
        toast({
          title: "Error - Usuario duplicado",
          description: `El email ${formData.email} ya está en uso. Si este usuario fue eliminado anteriormente, contacta al administrador del sistema.`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Error",
        description: "Hubo un problema al crear el usuario: " + errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (profile: Profile) => {
    setCurrentProfile(profile);
    setFormData({
      email: profile.email || "",
      name: profile.name || "",
      lastname: profile.lastname || "",
      phone: profile.phone || "",
      role: profile.role || "seller",
      password: "", // We don't show or change password during edit
    });
    setIsEditDialogOpen(true);
  };

  const handleDeactivate = async (profile: Profile) => {
    if (profile.id === currentUserId) {
      toast({
        title: "Acción no permitida",
        description: "No puedes desactivar tu propia cuenta",
        variant: "destructive",
      });
      return;
    }

    if (profile.role === "superadmin" && currentUserRole !== "superadmin") {
      toast({
        title: "Permiso denegado",
        description:
          "Solo un superadmin puede desactivar cuentas de superadmin",
        variant: "destructive",
      });
      return;
    }

    // Check if user is already deactivated
    if (!profile.is_active) {
      toast({
        title: "Usuario ya desactivado",
        description: "Este usuario ya está desactivado",
        variant: "default",
      });
      return;
    }

    // Open the deactivation reason dialog
    setProfileToDeactivate(profile);
    setDeactivateReason("");
    setIsDeactivateDialogOpen(true);
  };

  const confirmDeactivate = async () => {
    if (!profileToDeactivate) return;

    try {
      setDeactivateLoading(true);
      const { error } = await supabase.rpc("deactivate_user", {
        user_id: profileToDeactivate.id,
        reason: deactivateReason.trim() || null,
      });

      if (error) throw error;

      toast({
        title: "Usuario desactivado",
        description: "El usuario ha sido desactivado correctamente.",
      });

      closeDeactivateDialog();

      // Refresh the users list
      fetchProfiles();
    } catch (error) {
      console.error("Error deactivating user:", error);
      toast({
        title: "Error",
        description: "No se pudo desactivar el usuario",
        variant: "destructive",
      });
    } finally {
      setDeactivateLoading(false);
    }
  };

  const closeDeactivateDialog = () => {
    setIsDeactivateDialogOpen(false);
    setProfileToDeactivate(null);
    setDeactivateReason("");
  };

  const handleReactivate = async (profile: Profile) => {
    if (profile.role === "superadmin" && currentUserRole !== "superadmin") {
      toast({
        title: "Permiso denegado",
        description: "Solo un superadmin puede reactivar cuentas de superadmin",
        variant: "destructive",
      });
      return;
    }

    // Check if user is already active
    if (profile.is_active) {
      toast({
        title: "Usuario ya activo",
        description: "Este usuario ya está activo",
        variant: "default",
      });
      return;
    }

    try {
      const { data, error } = await supabase.rpc("reactivate_user", {
        user_id: profile.id,
      });

      if (error) throw error;

      toast({
        title: "Usuario reactivado",
        description: "El usuario ha sido reactivado correctamente.",
      });

      // Refresh the users list
      fetchProfiles();
    } catch (error) {
      console.error("Error reactivating user:", error);
      toast({
        title: "Error",
        description: "No se pudo reactivar el usuario",
        variant: "destructive",
      });
    }
  };

  const confirmDelete = async () => {
    if (!profileToDelete) return;

    try {
      setDeleteLoading(true);
      console.log("Attempting to delete user:", profileToDelete.id);

      // Try a direct database deletion first (more reliable)
      // Delete directly from the profile table
      const { error: directDeleteError } = await supabase
        .from("profiles")
        .delete()
        .eq("id", profileToDelete.id);

      if (directDeleteError) {
        console.error("Error with direct profile deletion:", directDeleteError);

        // Fall back to the RPC method if direct deletion fails
        console.log("Falling back to RPC delete method");

        // Use our secure RPC function for deletion which handles proper permissions
        // Use type assertion to bypass TypeScript error
        const { error: rpcError, data: rpcSuccess } = (await supabase.rpc(
          "delete_user",
          {
            user_id: profileToDelete.id,
          }
        )) as { data: boolean | null; error: Error | null };

        if (rpcError) {
          console.error("Error calling delete_user RPC:", rpcError);

          // Check if this is a permissions issue
          if (
            rpcError.message?.includes("Permission denied") ||
            (rpcError as { code?: string }).code === "not_admin" ||
            rpcError.message?.includes("not allowed")
          ) {
            toast({
              title: "Permiso denegado",
              description:
                "No tienes permisos de administrador para realizar esta acción.",
              variant: "destructive",
            });
            return;
          }

          // Generic error for other issues
          toast({
            title: "Error",
            description: "No se pudo eliminar el usuario: " + rpcError.message,
            variant: "destructive",
          });
          return;
        }

        console.log("RPC deletion result:", rpcSuccess);
      } else {
        console.log("Direct profile deletion successful");

        // Try to clean up the auth.users record if possible
        try {
          const { error: authDeleteError } =
            await supabase.auth.admin.deleteUser(profileToDelete.id);

          if (authDeleteError) {
            console.log(
              "Note: Could not delete from auth.users:",
              authDeleteError
            );
            // Not critical - we'll just log this
          }
        } catch (authDeleteErr) {
          console.log(
            "Note: Error when trying to delete from auth.users:",
            authDeleteErr
          );
          // Not critical - we'll just log this
        }
      }

      // Update the UI to reflect the deletion
      setProfiles(profiles.filter((p) => p.id !== profileToDelete.id));

      // Show appropriate success message based on user role
      const successMessage =
        profileToDelete.role === "seller"
          ? "El usuario vendedor ha sido eliminado correctamente. Sus asignaciones de clientes han sido removidas automáticamente."
          : "El perfil del usuario ha sido eliminado correctamente del sistema.";

      toast({
        title: "Usuario eliminado",
        description: successMessage,
      });

      closeDeleteDialog();

      // Force a refresh of the profiles after deletion
      setTimeout(() => {
        fetchProfiles();
      }, 500);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Error desconocido";
      console.error("Error deleting user:", error);

      // Check if the error contains the not_admin code or is a 500 error (recursion issue)
      if (
        error instanceof Error &&
        (error.message?.includes("not_admin") ||
          error.message?.includes("not allowed") ||
          error.message?.includes("Permission denied"))
      ) {
        toast({
          title: "Permiso denegado",
          description:
            "No tienes permisos suficientes para esta acción. Contacta al administrador del sistema.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description:
            "Hubo un problema al eliminar el usuario: " + errorMessage,
          variant: "destructive",
        });
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setProfileToDelete(null);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentProfile) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          name: formData.name,
          lastname: formData.lastname,
          phone: formData.phone,
          role: formData.role,
        })
        .eq("id", currentProfile.id);

      if (error) {
        console.error("Error updating profile:", error);
        toast({
          title: "Error",
          description: "No se pudo actualizar el perfil del usuario",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Perfil actualizado",
        description: "El perfil se ha actualizado correctamente",
      });

      resetForm();
      setIsEditDialogOpen(false);
      fetchProfiles();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Hubo un problema al actualizar el perfil",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      name: "",
      lastname: "",
      phone: "",
      role: "seller" as UserRole,
      password: "",
    });
    setIsDialogOpen(false);
    setCurrentProfile(null);
  };

  return {
    profiles,
    loading,
    isDialogOpen,
    setIsDialogOpen,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isDeactivateDialogOpen,
    setIsDeactivateDialogOpen,

    isChangePasswordDialogOpen,
    setIsChangePasswordDialogOpen,
    currentProfile,
    profileToDelete,
    profileToDeactivate,
    deactivateReason,
    setDeactivateReason,
    deactivateLoading,
    profileToChangePassword,
    deleteLoading,
    changePasswordLoading,
    formData,
    selectedProfiles,

    handleInputChange,
    handleRoleChange,
    handleSubmit,
    handleEdit,
    handleDeactivate,
    handleReactivate,
    confirmDeactivate,
    closeDeactivateDialog,
    handleChangePassword,
    confirmDelete,
    confirmChangePassword,
    closeDeleteDialog,
    closeChangePasswordDialog,
    toggleProfileSelection,
    toggleAllProfiles,

    handleUpdateProfile,
    fetchProfiles,
    resetForm,
  };
};
